import {
  useState,
  useTransition,
  type ReactNode,
} from "react";
import { useNavigate as useRouter } from "../../router";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { Input } from "../../input";
import { SettingsSection } from "./settings-section";

export interface PipelineStageRow {
  id: number;
  pipeline_id: number;
  slug: string;
  name: string;
  position: number;
}

export interface PipelineRow<S extends PipelineStageRow = PipelineStageRow> {
  id: number;
  slug: string;
  name: string;
  is_default?: number | boolean;
  stages: S[];
}

export interface NewStageDraft {
  slug: string;
  name: string;
  /** Extras consumed by renderNewStageExtras + onStageCreate. */
  extras: Record<string, unknown>;
}

export interface PipelineEditorProps<
  S extends PipelineStageRow,
  P extends PipelineRow<S>,
> {
  title?: ReactNode;
  description?: ReactNode;
  pipelines: P[];
  /** Initial extras for a fresh stage draft (e.g. `{ kind: "active" }`). */
  newStageDefaults?: Record<string, unknown>;
  /** Per-stage descriptor rendered before the editable name field. */
  renderStageBadge?: (stage: S) => ReactNode;
  /** Extra controls in the "Add stage" row. */
  renderNewStageExtras?: (
    extras: Record<string, unknown>,
    setExtras: (patch: Record<string, unknown>) => void,
  ) => ReactNode;
  /** Whether the "Set default" action is available on each pipeline. */
  allowSetDefault?: boolean;
  onPipelineCreate: (input: { slug: string; name: string }) => Promise<void>;
  onPipelineSetDefault?: (id: number) => Promise<void>;
  onStageCreate: (pipelineId: number, draft: NewStageDraft) => Promise<void>;
  onStageUpdate: (
    pipelineId: number,
    stageId: number,
    patch: { name?: string },
  ) => Promise<void>;
  onStageArchive: (pipelineId: number, stageId: number) => Promise<void>;
}

export function PipelineEditor<
  S extends PipelineStageRow,
  P extends PipelineRow<S>,
>({
  title = "Pipelines",
  description,
  pipelines,
  newStageDefaults = {},
  renderStageBadge,
  renderNewStageExtras,
  allowSetDefault = true,
  onPipelineCreate,
  onPipelineSetDefault,
  onStageCreate,
  onStageUpdate,
  onStageArchive,
}: PipelineEditorProps<S, P>) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [newPipeline, setNewPipeline] = useState({ slug: "", name: "" });
  const [stageDrafts, setStageDrafts] = useState<
    Record<number, NewStageDraft>
  >({});

  function freshDraft(): NewStageDraft {
    return { slug: "", name: "", extras: { ...newStageDefaults } };
  }

  function getDraft(pid: number): NewStageDraft {
    return stageDrafts[pid] ?? freshDraft();
  }

  function setDraft(pid: number, patch: Partial<NewStageDraft>) {
    setStageDrafts((prev) => {
      const cur = prev[pid] ?? freshDraft();
      return { ...prev, [pid]: { ...cur, ...patch } };
    });
  }

  function setExtras(pid: number, patch: Record<string, unknown>) {
    setStageDrafts((prev) => {
      const cur = prev[pid] ?? freshDraft();
      return {
        ...prev,
        [pid]: { ...cur, extras: { ...cur.extras, ...patch } },
      };
    });
  }

  function run<T>(fn: () => Promise<T>) {
    setError(null);
    startTransition(async () => {
      try {
        await fn();
        router.refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed.");
      }
    });
  }

  return (
    <SettingsSection title={title} description={description}>
      <div className="flex flex-col">
        {pipelines.map((p) => (
          <div
            key={p.id}
            className="space-y-2 border-b border-border/40 py-4 first:pt-0"
          >
              <div className="flex items-center justify-between">
                <div className="type-body text-foreground">
                  {p.name}{" "}
                  <span className="text-muted-foreground">/{p.slug}</span>
                  {p.is_default ? (
                    <Badge variant="outline" className="ml-2 type-body">
                      default
                    </Badge>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className="type-body text-muted-foreground">
                    {p.stages.length} stage{p.stages.length === 1 ? "" : "s"}
                  </span>
                  {allowSetDefault &&
                  onPipelineSetDefault &&
                  !p.is_default ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={pending}
                      onClick={() =>
                        run(() => onPipelineSetDefault!(p.id))
                      }
                    >
                      Set default
                    </Button>
                  ) : null}
                </div>
              </div>

              {p.stages.length === 0 ? (
                <div className="type-body text-muted-foreground">
                  No stages yet.
                </div>
              ) : (
                <ul className="flex flex-col">
                  {p.stages.map((s, idx) => (
                    <li
                      key={s.id}
                      className={
                        idx === p.stages.length - 1
                          ? "flex items-center justify-between gap-2 py-2 first:pt-0"
                          : "flex items-center justify-between gap-2 border-b border-border/40 py-2 first:pt-0"
                      }
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {renderStageBadge ? renderStageBadge(s) : null}
                        <Input
                          defaultValue={s.name}
                          className="max-w-56"
                          onBlur={(e) => {
                            if (e.target.value !== s.name) {
                              run(() =>
                                onStageUpdate(p.id, s.id, {
                                  name: e.target.value,
                                }),
                              );
                            }
                          }}
                        />
                        <span className="type-body text-muted-foreground">
                          slug: {s.slug} · position {s.position}
                        </span>
                      </div>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={pending}
                        onClick={() =>
                          run(() => onStageArchive(p.id, s.id))
                        }
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end pt-1">
                <Input
                  placeholder="Stage name"
                  value={getDraft(p.id).name}
                  onChange={(e) => setDraft(p.id, { name: e.target.value })}
                />
                <Input
                  placeholder="slug"
                  value={getDraft(p.id).slug}
                  onChange={(e) => setDraft(p.id, { slug: e.target.value })}
                />
                <div className="flex gap-2">
                  {renderNewStageExtras
                    ? renderNewStageExtras(
                        getDraft(p.id).extras,
                        (patch) => setExtras(p.id, patch),
                      )
                    : null}
                  <Button
                    size="sm"
                    disabled={
                      pending ||
                      !getDraft(p.id).name ||
                      !getDraft(p.id).slug
                    }
                    onClick={() => {
                      const draft = getDraft(p.id);
                      run(async () => {
                        await onStageCreate(p.id, draft);
                        setStageDrafts((prev) => ({
                          ...prev,
                          [p.id]: freshDraft(),
                        }));
                      });
                    }}
                  >
                    Add stage
                  </Button>
                </div>
              </div>
          </div>
        ))}

        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end pt-4">
          <Input
            placeholder="New pipeline name"
            value={newPipeline.name}
            onChange={(e) =>
              setNewPipeline({ ...newPipeline, name: e.target.value })
            }
          />
          <Input
            placeholder="slug"
            value={newPipeline.slug}
            onChange={(e) =>
              setNewPipeline({ ...newPipeline, slug: e.target.value })
            }
          />
          <Button
            size="sm"
            disabled={pending || !newPipeline.name || !newPipeline.slug}
            onClick={() => {
              const snapshot = newPipeline;
              run(async () => {
                await onPipelineCreate(snapshot);
                setNewPipeline({ slug: "", name: "" });
              });
            }}
          >
            Add pipeline
          </Button>
        </div>

        {error ? <p className="type-body text-destructive">{error}</p> : null}
      </div>
    </SettingsSection>
  );
}
